/**
 * API Error Handler
 *
 * Centralized error handling for RTK Query responses.
 * Extracts error messages from backend responses.
 * Handles 401 Unauthorized, validation errors, and generic errors.
 */

import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Extract error message from API error response
 *
 * Handles multiple error formats:
 * - ValidationException: multiple fields with error arrays
 * - Generic error message
 * - HTTP status code message
 *
 * @param error - RTK Query error or custom ApiError
 * @returns Formatted error message string
 */
export const getErrorMessage = (error: unknown): string => {
  // RTK Query FetchBaseQueryError
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const fetchError = error as FetchBaseQueryError;

    // HTTP status-specific error messages (priority over generic payload message)
    if (fetchError.status === 401) {
      return 'Session expired. Please login again.';
    }

    if (fetchError.status === 403) {
      if (fetchError.data && typeof fetchError.data === 'object') {
        const errorData = fetchError.data as any;
        if (errorData.errorCode === 'PASSWORD_CHANGE_REQUIRED') {
          return 'Wymagana zmiana hasła. Zostaniesz przekierowany.';
        }
      }
      return 'You do not have permission to perform this action.';
    }

    if (fetchError.status === 404) {
      return 'Resource not found.';
    }

    if (fetchError.status === 500) {
      return 'Server error. Please try again later.';
    }

    // Server returned error response
    if (fetchError.data && typeof fetchError.data === 'object') {
      const errorData = fetchError.data as any;

      // Backend validation error with multiple fields
      if (errorData.details && typeof errorData.details === 'object') {
        const fieldErrors = Object.entries(errorData.details)
          .map(([field, errors]: [string, any]) => {
            const errorArray = Array.isArray(errors) ? errors : [errors];
            return `${field}: ${errorArray.join(', ')}`;
          })
          .join(' | ');
        return fieldErrors || 'Validation error occurred';
      }

      // Single error message
      if (errorData.message) {
        return errorData.message;
      }

      if (errorData.error) {
        return errorData.error;
      }
    }

    return `Error: ${fetchError.status}`;
  }

  // Generic error object
  if (error instanceof Error) {
    return error.message;
  }

  // String error
  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
};

/**
 * Check if error is 401 Unauthorized
 *
 * @param error - RTK Query error
 * @returns true if error status is 401
 */
export const is401Error = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const fetchError = error as FetchBaseQueryError;
    return fetchError.status === 401;
  }
  return false;
};

/**
 * Check if error is validation error (422/400 with details)
 *
 * @param error - RTK Query error
 * @returns true if error contains validation details
 */
export const isValidationError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const fetchError = error as FetchBaseQueryError;
    if (fetchError.data && typeof fetchError.data === 'object') {
      const errorData = fetchError.data as any;
      return !!(errorData.details && typeof errorData.details === 'object');
    }
  }
  return false;
};

/**
 * Check if error is 403 PASSWORD_CHANGE_REQUIRED
 *
 * @param error - RTK Query error
 * @returns true if error status is 403 and errorCode is PASSWORD_CHANGE_REQUIRED
 */
export const isPasswordChangeRequiredError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const fetchError = error as FetchBaseQueryError;
    if (fetchError.status === 403 && fetchError.data && typeof fetchError.data === 'object') {
      const errorData = fetchError.data as any;
      return errorData.errorCode === 'PASSWORD_CHANGE_REQUIRED';
    }
  }
  return false;
};
